import React, { useState, useEffect, useRef } from 'react';
import * as mediasoupClient from 'mediasoup-client';
import { useNotification } from './NotificationSystem';
import './VoiceChannel.css';

function VoiceChannel({ socket, channel, user, onConnect, onDisconnect, isVisible = true, onAudioControlsReady }) {
  const { showSuccess, showError, confirm } = useNotification();
  const [inVoice, setInVoice] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [voiceUsers, setVoiceUsers] = useState([]);
  const [speakingUsers, setSpeakingUsers] = useState(new Set());
  const [selectedUser, setSelectedUser] = useState(null);
  const [userVolumes, setUserVolumes] = useState({});
  const [mutedUsers, setMutedUsers] = useState(new Set());
  const [enlargedVideo, setEnlargedVideo] = useState(null); // {userId, streamIndex, isScreenShare}
  
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);
  const producerRef = useRef(null);
  const videoProducerRef = useRef(null);
  const screenProducerRef = useRef(null);
  const consumersRef = useRef(new Map()); // producerId -> consumer
  const streamRef = useRef(null);
  const videoStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioElementsRef = useRef({});
  const videoElementsRef = useRef({});
  const videoRefsRef = useRef({}); // Stable video element refs to prevent flickering

  useEffect(() => {
    console.log('VoiceChannel mounted/updated. Channel:', channel?.id, 'inVoice:', inVoice);
    if (!socket || !channel || channel.type !== 'voice') return;

    const handleServerShutdown = () => {
      console.log('Server shutdown detected, forcing voice disconnect');
      // Clean up voice state immediately
      setInVoice(false);
      setVoiceUsers([]);
      if (onDisconnect) onDisconnect();
    };

    const handleDisconnect = (reason) => {
      console.log('Socket disconnected:', reason);
      if ((reason === 'transport close' || reason === 'transport error') && inVoice) {
        console.log('Unexpected disconnect while in voice, cleaning up');
        // Clean up voice state immediately
        setInVoice(false);
        setVoiceUsers([]);
        if (onDisconnect) onDisconnect();
      }
    };

    socket.on('existingProducers', handleExistingProducers);
    socket.on('newProducer', handleNewProducer);
    socket.on('user_joined_voice', handleUserJoined);
    socket.on('user_left_voice', handleUserLeft);
    socket.on('server_shutdown', handleServerShutdown);
    socket.on('disconnect', handleDisconnect);

    return () => {
      console.log('VoiceChannel cleanup. Channel:', channel?.id);
      socket.off('existingProducers', handleExistingProducers);
      socket.off('newProducer', handleNewProducer);
      socket.off('user_joined_voice', handleUserJoined);
      socket.off('user_left_voice', handleUserLeft);
      socket.off('server_shutdown', handleServerShutdown);
      socket.off('disconnect', handleDisconnect);
      
      // Don't auto-disconnect on unmount - only when explicitly leaving
    };
  }, [socket, channel, inVoice, onDisconnect]);

  // Voice activity detection
  useEffect(() => {
    if (!inVoice || !streamRef.current) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(streamRef.current);
    
    analyser.fftSize = 256;
    microphone.connect(analyser);
    
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let speaking = false;

    const checkAudioLevel = () => {
      if (!analyserRef.current) return;
      
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      
      const isSpeaking = average > 20;
      
      if (isSpeaking !== speaking) {
        speaking = isSpeaking;
        setSpeakingUsers(prev => {
          const newSet = new Set(prev);
          if (speaking) {
            newSet.add(user.id);
          } else {
            newSet.delete(user.id);
          }
          return newSet;
        });
      }
      
      if (inVoice) {
        requestAnimationFrame(checkAudioLevel);
      }
    };

    checkAudioLevel();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [inVoice, user.id]);

  // Listen for audio settings changes
  useEffect(() => {
    const handleSettingsChange = (event) => {
      const settings = event.detail;
      
      // Update all existing audio elements with new output settings
      Object.entries(audioElementsRef.current).forEach(([userId, audioEl]) => {
        if (audioEl) {
          const baseVolume = userVolumes[userId] !== undefined ? userVolumes[userId] : 1.0;
          const outputGain = settings.outputGain || 100;
          audioEl.volume = Math.min(1.0, (baseVolume * outputGain) / 100);
          
          // Update output device if supported
          if (settings.audioOutput && audioEl.setSinkId) {
            audioEl.setSinkId(settings.audioOutput).catch(err => {
              console.warn('Failed to set audio output device:', err);
            });
          }
        }
      });
    };
    
    window.addEventListener('audioSettingsChanged', handleSettingsChange);
    return () => window.removeEventListener('audioSettingsChanged', handleSettingsChange);
  }, [userVolumes]);

  const handleExistingProducers = async ({ producers }) => {
    console.log('Existing producers:', producers);
    
    // Wait a bit for recv transport to be ready
    if (!recvTransportRef.current) {
      console.warn('Receive transport not ready yet, waiting...');
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Group producers by userId to avoid duplicates
    const userMap = new Map();
    producers.forEach(p => {
      if (!userMap.has(p.userId)) {
        userMap.set(p.userId, { userId: p.userId, username: p.username });
      }
    });
    
    // Add existing users to voice users list (unique users only)
    setVoiceUsers(prev => {
      const existingUserIds = new Set(prev.map(u => u.userId));
      const newUsers = Array.from(userMap.values()).filter(u => !existingUserIds.has(u.userId));
      return [...prev, ...newUsers];
    });

    // Consume each existing producer
    for (const producer of producers) {
      await consumeProducer(producer.producerId, producer.userId);
    }
  };

  const handleNewProducer = async ({ producerId, userId, username }) => {
    console.log('New producer:', username, producerId);
    
    // Add user to voice users list
    setVoiceUsers(prev => {
      if (prev.find(u => u.userId === userId)) return prev;
      return [...prev, { userId, username }];
    });

    // Consume the new producer
    await consumeProducer(producerId, userId);
  };

  const handleUserJoined = ({ userId, username, channelId }) => {
    if (channelId !== channel.id || userId === user.id) return;
    console.log('User joined voice:', username);
  };

  const handleUserLeft = ({ userId, channelId }) => {
    if (channelId !== channel.id) return;
    console.log('User left voice:', userId);
    
    setVoiceUsers(prev => prev.filter(u => u.userId !== userId));
    
    // Remove audio elements
    if (audioElementsRef.current[userId]) {
      audioElementsRef.current[userId].remove();
      delete audioElementsRef.current[userId];
    }
    
    // Close consumers
    for (const [producerId, consumer] of consumersRef.current.entries()) {
      if (consumer.appData.userId === userId) {
        consumer.close();
        consumersRef.current.delete(producerId);
      }
    }
  };

  const joinVoice = async () => {
    try {
      console.log('joinVoice called. Current inVoice state:', inVoice);
      console.log('Requesting microphone access...');
      
      // Load audio settings from localStorage
      const settings = JSON.parse(localStorage.getItem('audioSettings') || '{}');
      
      const constraints = {
        audio: {
          echoCancellation: settings.echoCancellation !== false,
          noiseSuppression: settings.noiseSuppression !== false,
          autoGainControl: true
        },
        video: false
      };
      
      // Apply device ID if specified
      if (settings.audioInput) {
        constraints.audio.deviceId = { exact: settings.audioInput };
      }
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      console.log('Got microphone stream');
      
      // Apply input gain if specified
      if (settings.inputGain && settings.inputGain !== 100) {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaStreamSource(stream);
        const gainNode = audioContext.createGain();
        gainNode.gain.value = settings.inputGain / 100;
        const destination = audioContext.createMediaStreamDestination();
        
        source.connect(gainNode);
        gainNode.connect(destination);
        streamRef.current = destination.stream;
      } else {
        streamRef.current = stream;
      }

      // Initialize mediasoup device
      deviceRef.current = new mediasoupClient.Device();
      
      // Get router RTP capabilities
      const routerRtpCapabilities = await new Promise((resolve) => {
        socket.emit('getRouterRtpCapabilities', resolve);
      });

      await deviceRef.current.load({ routerRtpCapabilities });
      console.log('Mediasoup device loaded');

      // Create send transport
      const sendTransportData = await new Promise((resolve) => {
        socket.emit('createWebRtcTransport', {
          channelId: channel.id,
          type: 'send'
        }, resolve);
      });

      if (!sendTransportData.success) {
        throw new Error('Failed to create send transport');
      }

      sendTransportRef.current = deviceRef.current.createSendTransport(sendTransportData.transport);

      // Handle send transport events
      sendTransportRef.current.on('connect', async ({ dtlsParameters }, callback, errback) => {
        try {
          const result = await new Promise((resolve) => {
            socket.emit('connectTransport', {
              transportId: sendTransportRef.current.id,
              dtlsParameters
            }, resolve);
          });

          if (result.success) {
            callback();
          } else {
            errback(new Error(result.error));
          }
        } catch (error) {
          errback(error);
        }
      });

      sendTransportRef.current.on('produce', async ({ kind, rtpParameters }, callback, errback) => {
        try {
          const result = await new Promise((resolve) => {
            socket.emit('produce', {
              channelId: channel.id,
              transportId: sendTransportRef.current.id,
              kind,
              rtpParameters
            }, resolve);
          });

          if (result.success) {
            callback({ id: result.producerId });
          } else {
            errback(new Error(result.error));
          }
        } catch (error) {
          errback(error);
        }
      });

      // Produce audio
      const audioTrack = stream.getAudioTracks()[0];
      producerRef.current = await sendTransportRef.current.produce({ track: audioTrack });
      console.log('Audio producer created');

      // Create receive transport
      const recvTransportData = await new Promise((resolve) => {
        socket.emit('createWebRtcTransport', {
          channelId: channel.id,
          type: 'recv'
        }, resolve);
      });

      if (!recvTransportData.success) {
        throw new Error('Failed to create receive transport');
      }

      recvTransportRef.current = deviceRef.current.createRecvTransport(recvTransportData.transport);

      recvTransportRef.current.on('connect', async ({ dtlsParameters }, callback, errback) => {
        try {
          const result = await new Promise((resolve) => {
            socket.emit('connectTransport', {
              transportId: recvTransportRef.current.id,
              dtlsParameters
            }, resolve);
          });

          if (result.success) {
            callback();
          } else {
            errback(new Error(result.error));
          }
        } catch (error) {
          errback(error);
        }
      });

      // Join voice channel and update state FIRST
      console.log('About to emit join_voice and set states');
      socket.emit('join_voice', { channelId: channel.id });
      console.log('Setting inVoice to true');
      setInVoice(true);
      
      // Add yourself to voice users list
      console.log('Adding self to voiceUsers');
      setVoiceUsers(prev => {
        if (prev.find(u => u.userId === user.id)) return prev;
        return [...prev, { userId: user.id, username: user.username }];
      });
      
      console.log('Joined voice channel');
      
      // Notify parent LAST after all state updates are queued
      console.log('Calling onConnect callback');
      if (onConnect) onConnect();
      console.log('joinVoice complete');

    } catch (error) {
      console.error('Error joining voice:', error);
      showError('Failed to join voice channel: ' + error.message);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  };

  const consumeProducer = async (producerId, userId) => {
    try {
      if (!recvTransportRef.current || !deviceRef.current) {
        console.warn('Cannot consume - transport not ready');
        return;
      }

      const result = await new Promise((resolve) => {
        socket.emit('consume', {
          channelId: channel.id,
          transportId: recvTransportRef.current.id,
          producerId,
          rtpCapabilities: deviceRef.current.rtpCapabilities
        }, resolve);
      });

      if (!result.success) {
        console.error('Failed to consume:', result.error);
        return;
      }

      const consumer = await recvTransportRef.current.consume({
        id: result.params.id,
        producerId: result.params.producerId,
        kind: result.params.kind,
        rtpParameters: result.params.rtpParameters
      });

      consumer.appData = { userId };
      consumersRef.current.set(producerId, consumer);

      if (consumer.kind === 'audio') {
        // Create audio element and play
        const audioEl = document.createElement('audio');
        audioEl.srcObject = new MediaStream([consumer.track]);
        audioEl.autoplay = true;
        
        // Load audio settings for output gain and device
        const settings = JSON.parse(localStorage.getItem('audioSettings') || '{}');
        const outputGain = settings.outputGain || 100;
        const baseVolume = userVolumes[userId] !== undefined ? userVolumes[userId] : 1.0;
        
        // Apply both output gain and user-specific volume
        audioEl.volume = Math.min(1.0, (baseVolume * outputGain) / 100);
        
        if (mutedUsers.has(userId)) {
          audioEl.muted = true;
        }
        
        // Set output device if supported and specified
        if (settings.audioOutput && audioEl.setSinkId) {
          audioEl.setSinkId(settings.audioOutput).catch(err => {
            console.warn('Failed to set audio output device:', err);
          });
        }

        document.body.appendChild(audioEl);
        audioElementsRef.current[userId] = audioEl;
        console.log('Audio consumer created for user:', userId);
      } else if (consumer.kind === 'video') {
        // Video will be rendered in the UI, stored by ref
        if (!videoElementsRef.current[userId]) {
          videoElementsRef.current[userId] = { streams: [] };
        }
        const stream = new MediaStream([consumer.track]);
        videoElementsRef.current[userId].streams.push(stream);
        
        // Attach to existing video element if it exists
        const videoRefs = videoRefsRef.current[userId];
        if (videoRefs && videoRefs[videoElementsRef.current[userId].streams.length - 1]) {
          videoRefs[videoElementsRef.current[userId].streams.length - 1].srcObject = stream;
        }
        
        // Force re-render
        setVoiceUsers(prev => [...prev]);
        console.log('Video consumer created for user:', userId);
      }

      consumer.on('transportclose', () => {
        consumer.close();
        consumersRef.current.delete(producerId);
      });

    } catch (error) {
      console.error('Error consuming producer:', error);
    }
  };

  const leaveVoice = () => {
    console.log('Leaving voice channel');

    // Close all producers
    if (producerRef.current) {
      producerRef.current.close();
      producerRef.current = null;
    }
    if (videoProducerRef.current) {
      videoProducerRef.current.close();
      videoProducerRef.current = null;
    }
    if (screenProducerRef.current) {
      screenProducerRef.current.close();
      screenProducerRef.current = null;
    }

    // Close all consumers
    for (const consumer of consumersRef.current.values()) {
      consumer.close();
    }
    consumersRef.current.clear();

    // Close transports
    if (sendTransportRef.current) {
      sendTransportRef.current.close();
      sendTransportRef.current = null;
    }
    
    if (recvTransportRef.current) {
      recvTransportRef.current.close();
      recvTransportRef.current = null;
    }

    // Stop local streams
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach(track => track.stop());
      videoStreamRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }

    // Remove audio/video elements
    Object.values(audioElementsRef.current).forEach(el => el.remove());
    audioElementsRef.current = {};
    videoElementsRef.current = {};

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    socket.emit('leave_voice', { channelId: channel.id });
    setInVoice(false);
    setVideoEnabled(false);
    setScreenSharing(false);
    setVoiceUsers([]);
    setSpeakingUsers(new Set());
    if (onDisconnect) onDisconnect();
  };

  const toggleMute = () => {
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = async () => {
    if (!inVoice || !sendTransportRef.current) return;

    try {
      if (videoEnabled) {
        // Stop video
        console.log('Stopping video, producer:', videoProducerRef.current?.id);
        
        if (videoStreamRef.current) {
          videoStreamRef.current.getTracks().forEach(track => {
            track.stop();
            console.log('Stopped video track');
          });
          videoStreamRef.current = null;
        }
        
        if (videoProducerRef.current) {
          const producerId = videoProducerRef.current.id;
          try {
            await videoProducerRef.current.close();
            socket.emit('closeProducer', { producerId });
            console.log('Closed video producer:', producerId);
          } catch (err) {
            console.error('Error closing video producer:', err);
          }
          videoProducerRef.current = null;
        }
        
        setVideoEnabled(false);
        console.log('Video stopped');
      } else {
        // Start video with reasonable quality
        // Load audio settings for camera selection
        const settings = JSON.parse(localStorage.getItem('audioSettings') || '{}');
        
        const videoConstraints = {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 24, max: 30 }
        };
        
        // Apply camera device ID if specified
        if (settings.videoInput) {
          videoConstraints.deviceId = { exact: settings.videoInput };
        }
        
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints
        });
        
        videoStreamRef.current = stream;
        const videoTrack = stream.getVideoTracks()[0];
        
        videoProducerRef.current = await sendTransportRef.current.produce({ 
          track: videoTrack,
          encodings: [
            { maxBitrate: 1000000 } // 1 Mbps
          ]
        });
        console.log('Video producer created:', videoProducerRef.current.id);
        setVideoEnabled(true);
      }
    } catch (error) {
      console.error('Error toggling video:', error);
      if (error.name !== 'NotAllowedError') {
        showError('Failed to toggle video: ' + error.message);
      }
      // Ensure state is consistent on error
      setVideoEnabled(false);
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop());
        videoStreamRef.current = null;
      }
      videoProducerRef.current = null;
    }
  };

  const toggleScreenShare = async () => {
    if (!inVoice || !sendTransportRef.current) return;

    try {
      if (screenSharing) {
        // Stop screen share
        console.log('Stopping screen share, producer:', screenProducerRef.current?.id);
        
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach(track => {
            track.stop();
            console.log('Stopped screen share track');
          });
          screenStreamRef.current = null;
        }
        
        if (screenProducerRef.current) {
          const producerId = screenProducerRef.current.id;
          try {
            await screenProducerRef.current.close();
            socket.emit('closeProducer', { producerId });
            console.log('Closed screen share producer:', producerId);
          } catch (err) {
            console.error('Error closing producer:', err);
          }
          screenProducerRef.current = null;
        }
        
        setScreenSharing(false);
        console.log('Screen share stopped');
      } else {
        // Start screen share with lower quality for better performance
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            frameRate: { ideal: 15, max: 30 }
          }
        });
        
        screenStreamRef.current = stream;
        const videoTrack = stream.getVideoTracks()[0];
        
        // Handle user stopping share via browser UI
        videoTrack.onended = async () => {
          console.log('Screen share track ended by user');
          if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(track => track.stop());
            screenStreamRef.current = null;
          }
          if (screenProducerRef.current) {
            const producerId = screenProducerRef.current.id;
            try {
              await screenProducerRef.current.close();
              socket.emit('closeProducer', { producerId });
            } catch (err) {
              console.error('Error closing producer:', err);
            }
            screenProducerRef.current = null;
          }
          setScreenSharing(false);
        };
        
        screenProducerRef.current = await sendTransportRef.current.produce({ 
          track: videoTrack,
          encodings: [
            { maxBitrate: 1500000 } // 1.5 Mbps for better quality/performance balance
          ],
          codecOptions: {
            videoGoogleStartBitrate: 1000
          }
        });
        console.log('Screen share producer created:', screenProducerRef.current.id);
        setScreenSharing(true);
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
      if (error.name !== 'NotAllowedError') {
        showError('Failed to share screen: ' + error.message);
      }
      // Ensure state is consistent on error
      setScreenSharing(false);
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }
      screenProducerRef.current = null;
    }
  };

  const handleVolumeChange = (userId, volume) => {
    setUserVolumes(prev => ({ ...prev, [userId]: volume }));
    if (audioElementsRef.current[userId]) {
      const settings = JSON.parse(localStorage.getItem('audioSettings') || '{}');
      const outputGain = settings.outputGain || 100;
      audioElementsRef.current[userId].volume = Math.min(1.0, (volume * outputGain) / 100);
    }
  };

  const toggleUserMute = (userId) => {
    setMutedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
        if (audioElementsRef.current[userId]) {
          audioElementsRef.current[userId].muted = false;
        }
      } else {
        newSet.add(userId);
        if (audioElementsRef.current[userId]) {
          audioElementsRef.current[userId].muted = true;
        }
      }
      return newSet;
    });
  };

  // Cleanup media resources when leaving voice
  useEffect(() => {
    if (!inVoice) {
      // Close all producers
      if (producerRef.current) {
        producerRef.current.close();
        producerRef.current = null;
      }
      if (videoProducerRef.current) {
        videoProducerRef.current.close();
        videoProducerRef.current = null;
      }
      if (screenProducerRef.current) {
        screenProducerRef.current.close();
        screenProducerRef.current = null;
      }

      // Close all consumers
      for (const consumer of consumersRef.current.values()) {
        consumer.close();
      }
      consumersRef.current.clear();

      // Close transports
      if (sendTransportRef.current) {
        sendTransportRef.current.close();
        sendTransportRef.current = null;
      }
      if (recvTransportRef.current) {
        recvTransportRef.current.close();
        recvTransportRef.current = null;
      }

      // Stop local streams
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop());
        videoStreamRef.current = null;
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }

      // Close audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // Remove audio/video elements
      Object.values(audioElementsRef.current).forEach(el => el?.remove());
      audioElementsRef.current = {};
      videoElementsRef.current = {};
      videoRefsRef.current = {};
    }
  }, [inVoice]);

  // Expose audio controls to parent
  useEffect(() => {
    if (onAudioControlsReady && inVoice) {
      onAudioControlsReady({
        setVolume: handleVolumeChange,
        toggleMute: toggleUserMute
      });
    }
  }, [inVoice, onAudioControlsReady]);

  if (!channel || channel.type !== 'voice') {
    return null;
  }

  console.log('Rendering VoiceChannel. inVoice:', inVoice, 'channel:', channel?.id, 'isVisible:', isVisible);

  return (
    <div className="voice-channel" style={{ display: isVisible ? 'flex' : 'none' }}>
      <div className="voice-header">
        <div className="voice-header-left">
          <span className="voice-icon">🔊</span>
          <h2 className="voice-title">{channel.name}</h2>
        </div>
        <div className="voice-actions">
          {!inVoice && (
            <button onClick={joinVoice} className="voice-button join">Join Voice</button>
          )}
        </div>
      </div>

      {inVoice && (
        <div className="voice-grid">
          {/* Current user card */}
          <div className={`voice-card ${speakingUsers.has(user.id) ? 'speaking' : ''}`}>
            {videoEnabled && videoStreamRef.current && (
              <video
                ref={el => {
                  if (el && el.srcObject !== videoStreamRef.current) {
                    el.srcObject = videoStreamRef.current;
                  }
                }}
                autoPlay
                playsInline
                muted
                className="voice-video"
                onClick={() => setEnlargedVideo({ userId: user.id, isOwnCamera: true })}
              />
            )}
            {screenSharing && screenStreamRef.current && (
              <video
                ref={el => {
                  if (el && el.srcObject !== screenStreamRef.current) {
                    el.srcObject = screenStreamRef.current;
                  }
                }}
                autoPlay
                playsInline
                muted
                className="voice-video screen-share"
                onClick={() => setEnlargedVideo({ userId: user.id, isOwnScreen: true })}
              />
            )}
            {!videoEnabled && !screenSharing && (
              <div className="voice-card-avatar">{user.username[0].toUpperCase()}</div>
            )}
            <div className="voice-card-name">{user.username} (You) {muted && '🔇'}</div>
          </div>

          {/* Other users */}
          {voiceUsers.filter(u => u.username && u.userId !== user.id).map(voiceUser => {
            // Initialize stable refs for this user
            if (!videoRefsRef.current[voiceUser.userId]) {
              videoRefsRef.current[voiceUser.userId] = {};
            }
            
            return (
              <div 
                key={voiceUser.userId}
                className={`voice-card ${speakingUsers.has(voiceUser.userId) ? 'speaking' : ''}`}
              >
                {videoElementsRef.current[voiceUser.userId]?.streams?.length > 0 ? (
                  videoElementsRef.current[voiceUser.userId].streams.map((stream, idx) => (
                    <video
                      key={idx}
                      ref={el => {
                        if (el) {
                          videoRefsRef.current[voiceUser.userId][idx] = el;
                          if (el.srcObject !== stream) {
                            el.srcObject = stream;
                          }
                        }
                      }}
                      autoPlay
                      playsInline
                      className="voice-video"
                      onClick={() => setEnlargedVideo({ userId: voiceUser.userId, streamIndex: idx })}
                    />
                  ))
                ) : (
                  <div className="voice-card-avatar">{voiceUser.username?.[0]?.toUpperCase() || '?'}</div>
                )}
                <div className="voice-card-name">
                  {voiceUser.username}
                  <div className="voice-card-controls">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleUserMute(voiceUser.userId);
                      }}
                      className="voice-card-control-btn"
                    >
                      {mutedUsers.has(voiceUser.userId) ? '🔇' : '🔊'}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={userVolumes[voiceUser.userId] || 1.0}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleVolumeChange(voiceUser.userId, parseFloat(e.target.value));
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="voice-volume-slider"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {inVoice && (
        <div className="voice-controls">
          <button onClick={toggleMute} className={`voice-button ${muted ? 'active' : 'unmute'}`}>
            {muted ? '🔇' : '🎤'}
          </button>
          <button onClick={toggleVideo} className={`voice-button ${videoEnabled ? 'active' : ''}`}>
            {videoEnabled ? '📹' : '📷'}
          </button>
          <button onClick={toggleScreenShare} className={`voice-button ${screenSharing ? 'active' : ''}`}>
            {screenSharing ? '🖥️' : '🖵'}
          </button>
          <button onClick={leaveVoice} className="voice-button leave">Leave</button>
        </div>
      )}

      {enlargedVideo && (
        <div className="video-modal" onClick={() => setEnlargedVideo(null)}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={() => setEnlargedVideo(null)}>
              ×
            </button>
            {enlargedVideo.isOwnCamera && videoStreamRef.current && (
              <video
                ref={el => el && el.srcObject !== videoStreamRef.current && (el.srcObject = videoStreamRef.current)}
                autoPlay
                playsInline
                muted
                className="video-modal-video"
              />
            )}
            {enlargedVideo.isOwnScreen && screenStreamRef.current && (
              <video
                ref={el => el && el.srcObject !== screenStreamRef.current && (el.srcObject = screenStreamRef.current)}
                autoPlay
                playsInline
                muted
                className="video-modal-video"
              />
            )}
            {enlargedVideo.userId && !enlargedVideo.isOwnCamera && !enlargedVideo.isOwnScreen && 
             videoElementsRef.current[enlargedVideo.userId]?.streams?.[enlargedVideo.streamIndex] && (
              <video
                ref={el => {
                  if (el) {
                    const stream = videoElementsRef.current[enlargedVideo.userId].streams[enlargedVideo.streamIndex];
                    if (el.srcObject !== stream) {
                      el.srcObject = stream;
                    }
                  }
                }}
                autoPlay
                playsInline
                className="video-modal-video"
              />
            )}
            <div className="video-modal-username">
              {enlargedVideo.isOwnCamera || enlargedVideo.isOwnScreen 
                ? `${user.username} (You)`
                : voiceUsers.find(u => u.userId === enlargedVideo.userId)?.username || 'Unknown'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VoiceChannel;
